<template>
  <div :class="{ 'conversation-detail-chronology--has-tabs': hasActions }" class="conversation-detail-chronology">
    <chronology
      :items="renderItems"
      :visibility-note="visibilityNote"
      :comment-submit-handler="commentSubmitHandler"
      :incoming-reply-submit-handler="incomingReplySubmitHandler"
      @original-message-requested="(replyInfo) => $emit('original-message-requested', replyInfo)"
    />
    <div v-if="hasHistory" class="dd-divider" />
    <comment-or-reply
      :entity-id="mailboxId"
      :item-id="conversationId"
      :is-submitting="isSubmitting"
      :is-traffic-cop-checking="isTrafficCopChecking"
      :is-post-submit-pre-refetch-data="isPostSubmitPreRefetchData"
      :author="profile"
      :reply-signature="replySignature"
      :responders-since-last-view="respondersSinceLastView"
      :customer="customer"
      :cc="cc"
      :saved-replies="savedReplies"
      :mention-users="mentionUsers"
      :assignee-id="assigneeId"
      :assignees-options="assigneesOptions"
      :priority-id="priorityId"
      :priority-options="priorityOptions"
      :status-id="statusId"
      :status-options="statusOptions"
      :due-date="dueDate as string"
      :watcher-ids="listWatchers"
      :watchers-options="watchersOptions"
      :job-info="jobInfo"
      class="conversation-detail-chronology__comment"
      @editor-opened="(val) => $emit('editor-opened', val)"
      @update-watchers="(ids) => $emit('update-watchers', ids)"
      @update-cc="(ccs) => $emit('update-cc', ccs)"
      @submit="(data) => $emit('submit', data)"
      @confirmed-traffic-cop-note="$emit('confirmed-traffic-cop-note')"
    />
  </div>
</template>
<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import {
  IChronologyItem,
  IChronologyItemTypes,
} from '@/types/data-objects/item-histories'
import { IProfile } from '@/types/data-objects/profile'
import {
  IAtMentionUser,
  IConvertedSavedReply,
  IItemResponder,
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'
import { IJobInfo } from '@/types/data-objects/items'

/* API */

export default defineComponent({
  name: 'ConversationDetailChronology',

  props: {
    mailboxId: {
      type: Number,
      required: true,
    },

    conversationId: {
      type: Number,
      required: true,
    },

    items: {
      type: Array as () => IChronologyItem[],
      required: true,
    },

    isSubmitting: {
      type: Boolean,
      default: false,
    },

    isTrafficCopChecking: {
      type: Boolean,
      default: false,
    },

    isPostSubmitPreRefetchData: {
      type: Boolean,
      default: false,
    },

    cc: {
      type: Array as () => string[],
      default: (): string[] => [],
    },

    replySignature: {
      type: String,
      default: '',
    },

    customer: {
      type: Object as () => IPersonWithEmail,
      required: true,
    },

    /* Editor */
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      required: true,
    },

    /* Assignee picker */
    assigneeId: {
      type: Number,
      default: null,
    },

    /* Priority */
    priorityId: {
      type: Number,
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    /* Status */
    statusId: {
      type: Number,
      required: true,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    savedReplies: {
      type: Array as () => IConvertedSavedReply[],
      default: (): IConvertedSavedReply[] => [],
    },

    respondersSinceLastView: {
      type: Array as () => IItemResponder[],
      default: (): IItemResponder[] => [],
    },

    /* Due date */
    dueDate: {
      type: [Date, String],
      default: null,
    },

    /* Watchers picker */
    listWatchers: {
      type: Array as () => number[],
      required: true,
    },

    assigneesOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    watchersOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    profile: {
      type: Object as () => IProfile,
      required: true,
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    commentSubmitHandler: {
      type: Function,
      default: (): any => {},
    },

    incomingReplySubmitHandler: {
      type: Function,
      default: (): any => {},
    },
  },

  emits: [
    'confirmed-traffic-cop-note',
    'editor-opened',
    'original-message-requested',
    'submit',
    'update-cc',
    'update-watchers',
  ],

  data() {
    return {
      renderItems: [] as any[],
    }
  },

  computed: {
    visibilityNote(): string {
      return 'Hidden from customer'
    },
    hasHistory(): boolean {
      return this.renderItems && this.renderItems.length > 0
    },
    hasActions(): boolean {
      return this.renderItems.some(i => i.type === IChronologyItemTypes.ACTION)
    },
  },

  watch: {
    items: {
      deep: 1,
      handler() {
        this.renderItems = this.items
      },
    },
  },

  mounted() {
    this.renderItems = this.items
  },
})
</script>

<style lang="scss">
.conversation-detail-chronology {
  background-color: var(--color-background-primary);
  border-top: $border--solid-light;
  // Mimicking bottom curve of parent el-card
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  &--has-tabs {
    border-top: none;
  }

  &__comment {
    margin-top: 20px;

    &.comment-or-reply {
      padding: 24px $detail-gutter-pad-sm;

      @include media-breakpoint-up(md) {
        padding: 24px $detail-gutter-pad-md 64px $detail-gutter-pad-md;
      }

      @include media-breakpoint-up(lg) {
        padding: 24px $detail-gutter-pad-lg 64px $detail-gutter-pad-lg;
      }
    }
  }
}
</style>
